<template>
  <div class="addon-groups mt-2 mb-2">
    <div class="addon-groups-panel">
      <div class="d-flex justify-content-between align-items-center mb-2">
        <div>
          <h5 class="panel-title mb-0">Add-on Groups</h5>
          <small class="panel-hint">Group related add-ons with a selection rule. Add-ons not in any group behave exactly as today.</small>
        </div>
        <b-button variant="primary" class="add-group-btn" @click="addGroup">+ Add group</b-button>
      </div>

      <div v-if="!groups.length" class="empty-groups">
        No add-on groups yet. Click &ldquo;+ Add group&rdquo; to create one.
      </div>

      <div v-for="(group, index) in groups" :key="group.key" class="addon-group-card mb-2">
        <div class="group-head" @click="toggleCollapse(group)">
          <span class="grip" title="Reorder">&#8942;&#8942;</span>
          <span class="group-title-block">
            <strong class="group-title-text">{{ group.title || 'Untitled group' }}</strong>
            <small class="group-meta">
              {{ group.addon_ids.length }} add-on(s) &middot; max {{ group.max_count }} &middot;
              {{ group.is_mandatory ? 'mandatory' : 'optional' }} &middot;
              {{ group.default_addon_id ? 'default: ' + addonName(group.default_addon_id) : 'no default' }}
            </small>
          </span>
          <span class="spacer"></span>
          <span class="head-ico" :class="{disabled: index === 0}" title="Move up" @click.stop="moveGroup(index, -1)">&#9650;</span>
          <span class="head-ico" :class="{disabled: index === groups.length - 1}" title="Move down" @click.stop="moveGroup(index, 1)">&#9660;</span>
          <span class="head-ico chev" :title="group.collapsed ? 'Expand' : 'Collapse'">{{ group.collapsed ? '&#9660;' : '&#9650;' }}</span>
          <span class="head-ico danger" title="Delete group (add-ons return to ungrouped)" @click.stop="removeGroup(index)">&#128465;</span>
        </div>

        <div class="group-body" v-show="!group.collapsed">
          <CRow>
            <CCol class="col-8 mb-2">
              <label class="required">Group Title</label>
              <b-form-input v-model="group.title" placeholder="e.g. Select fabric" :name="`group_title_${index}`" />
            </CCol>
            <CCol class="col-4 mb-2">
              <label class="required">Max Selections</label>
              <b-form-input type="number" min="1" :max="group.addon_ids.length || 1" v-model.number="group.max_count" />
            </CCol>
          </CRow>

          <table class="group-addons-table">
            <thead>
              <tr>
                <th>Add-on</th>
                <th class="c">Default</th>
                <th class="c w40"></th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="addon_id in group.addon_ids" :key="addon_id">
                <td>{{ addonName(addon_id) }}</td>
                <td class="c">
                  <input type="radio" :name="`default_${group.key}`" :checked="group.default_addon_id === addon_id"
                         @change="setDefault(group, addon_id)" />
                </td>
                <td class="c w40">
                  <span class="rm" title="Remove from group" @click="removeAddonFromGroup(group, addon_id)">&#10005;</span>
                </td>
              </tr>
              <tr>
                <td class="no-default">No default</td>
                <td class="c">
                  <input type="radio" :name="`default_${group.key}`" :checked="group.default_addon_id === null"
                         @change="setDefault(group, null)" />
                </td>
                <td></td>
              </tr>
            </tbody>
          </table>

          <div class="d-flex justify-content-between align-items-start mt-2">
            <div class="group-flags">
              <b-form-checkbox v-model="group.is_mandatory">
                Mandatory — user must select at least one add-on
              </b-form-checkbox>
              <b-form-checkbox v-model="group.hide_zero_price" class="mt-1">
                Hide $0.00 prices for this group&rsquo;s add-ons on the storefront
              </b-form-checkbox>
            </div>
            <div class="d-flex align-items-center add-addon-controls">
              <b-form-select v-model="group.pending_addon" :options="availableAddonSelectOptions(group)" size="sm" class="mr-2">
                <template #first>
                  <b-form-select-option :value="null" disabled>Add an add-on&hellip;</b-form-select-option>
                </template>
              </b-form-select>
              <b-button size="sm" variant="outline-primary" :disabled="!group.pending_addon" @click="addAddonToGroup(group)">Add</b-button>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
let groupKey = 0;

export default {
  name: "AddonGroups",
  props: {
    // Attached add-ons ({id, label}) — mirrors the Select Add-ons field
    attachedAddons: {
      type: Array,
      default: () => []
    },
    // Full addon catalog (same options as the Select Add-ons field)
    allAddons: {
      type: Array,
      default: () => []
    }
  },
  data() {
    return {
      groups: []
    };
  },
  watch: {
    // Keep the parent's individual add-ons dropdown in sync: any add-on used by
    // a group must disappear from that list (mutual exclusivity).
    groups: {
      deep: true,
      handler() {
        const ids = [];
        this.groups.forEach(g => g.addon_ids.forEach(id => {
          if (!ids.includes(id)) ids.push(id);
        }));
        this.$emit("grouped-addon-ids", ids);
      }
    }
  },
  methods: {
    setGroups(addon_groups, addons_with_pivot) {
      const members_by_group = {};
      (addons_with_pivot || []).forEach(addon => {
        const group_id = addon.pivot ? addon.pivot.group_id : null;
        if (group_id) {
          if (!members_by_group[group_id]) members_by_group[group_id] = [];
          members_by_group[group_id].push(addon.id);
        }
      });
      this.groups = (addon_groups || []).map(group => ({
        key: ++groupKey,
        id: group.id,
        title: group.title,
        max_count: group.max_count,
        is_mandatory: !!group.is_mandatory,
        hide_zero_price: !!group.hide_zero_price,
        default_addon_id: group.default_addon_id,
        addon_ids: members_by_group[group.id] || [],
        collapsed: false,
        pending_addon: null
      }));
    },
    addGroup() {
      this.groups.push({
        key: ++groupKey,
        id: null,
        title: "",
        max_count: 1,
        is_mandatory: false,
        hide_zero_price: false,
        default_addon_id: null,
        addon_ids: [],
        collapsed: false,
        pending_addon: null
      });
    },
    removeGroup(index) {
      this.groups.splice(index, 1);
    },
    moveGroup(index, direction) {
      const target = index + direction;
      if (target < 0 || target >= this.groups.length) return;
      const moved = this.groups.splice(index, 1)[0];
      this.groups.splice(target, 0, moved);
    },
    toggleCollapse(group) {
      this.$set(group, "collapsed", !group.collapsed);
    },
    findAddon(addon_id) {
      return this.attachedAddons.find(a => a.id === addon_id)
        || (this.allAddons || []).find(a => a.id === addon_id)
        || null;
    },
    // Labels look like "3D hummel Badge - $2.00" or "Dyed Rib Collar/sleeve: $5 (min 200 pcs)"
    splitLabel(addon_id) {
      const addon = this.findAddon(addon_id);
      const label = addon ? String(addon.label) : `#${addon_id}`;
      const match = label.match(/^(.*?)\s*[-:]\s*(\$[\d.,]+.*)$/);
      if (match) return { name: match[1], price: match[2] };
      return { name: label, price: "—" };
    },
    addonName(addon_id) {
      return this.splitLabel(addon_id).name;
    },
    // An add-on may belong to MULTIPLE groups, but not to a group and the
    // individual add-ons list at the same time. Exclude only add-ons already in
    // THIS group, or used individually (attachedAddons mirrors Select Add-ons).
    availableAddonSelectOptions(group) {
      const used = new Set(group.addon_ids);
      (this.attachedAddons || []).forEach(a => used.add(a.id));
      return (this.allAddons || [])
        .filter(addon => !used.has(addon.id))
        .map(addon => ({ value: addon.id, text: addon.label }));
    },
    addAddonToGroup(group) {
      const addon_id = group.pending_addon;
      if (!addon_id || group.addon_ids.includes(addon_id)) return;
      group.addon_ids.push(addon_id);
      group.pending_addon = null;
      if (!group.max_count || group.max_count < 1) group.max_count = 1;
    },
    removeAddonFromGroup(group, addon_id) {
      group.addon_ids = group.addon_ids.filter(id => id !== addon_id);
      if (group.default_addon_id === addon_id) group.default_addon_id = null;
      if (group.max_count > group.addon_ids.length) {
        group.max_count = Math.max(1, group.addon_ids.length);
      }
    },
    setDefault(group, addon_id) {
      group.default_addon_id = addon_id;
    },
    validate() {
      for (let i = 0; i < this.groups.length; i++) {
        const group = this.groups[i];
        const label = group.title ? `"${group.title}"` : `Group ${i + 1}`;
        if (!group.title || !group.title.trim()) {
          return `Group ${i + 1}: title is required`;
        }
        if (!group.addon_ids.length) {
          return `${label}: add at least one add-on`;
        }
        if (!group.max_count || group.max_count < 1 || group.max_count > group.addon_ids.length) {
          return `${label}: max selections must be between 1 and ${group.addon_ids.length}`;
        }
      }
      return null;
    },
    buildPayload() {
      return this.groups.map(group => ({
        id: group.id,
        title: group.title.trim(),
        max_count: group.max_count,
        is_mandatory: group.is_mandatory,
        hide_zero_price: group.hide_zero_price,
        default_addon_id: group.default_addon_id,
        addon_ids: group.addon_ids
      }));
    },
  }
};
</script>

<style scoped>
.addon-groups-panel {
  max-width: 100%;
}
.panel-title {
  color: #3c4b64;
  font-weight: 600;
}
.panel-hint {
  color: #8a93a2;
}
/* Matches the wizard's .addBtn (brand blue, bold, rounded, soft shadow) */
.add-group-btn {
  background: #009FE3 0% 0% no-repeat padding-box;
  border-color: #009FE3;
  box-shadow: 0px 3px 6px #00000029;
  border-radius: 14px;
  font-weight: bold;
  font-size: 1rem;
  padding: 6px 18px;
  color: #FFFFFF;
}
.add-group-btn:hover,
.add-group-btn:focus,
.add-group-btn:active {
  background: #008ecb !important;
  border-color: #008ecb !important;
  box-shadow: 0px 3px 6px #00000029 !important;
}
.empty-groups {
  border: 1px dashed #BCE0FD;
  border-radius: 10px;
  background: #F1F9FF;
  color: #8a93a2;
  text-align: center;
  padding: 12px;
  font-size: 14px;
}
/* Card language matches .product-details (radius 10, soft shadow, no border) */
.addon-group-card {
  border: none;
  border-radius: 10px;
  background: #fff;
  box-shadow: 0 0 13px 0 rgba(0, 0, 0, 0.13);
}
/* Same brand blue as the "+ Add group" button for consistency */
.group-head {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 8px 14px;
  background: #009FE3;
  border-bottom: none;
  border-radius: 10px 10px 0 0;
  cursor: pointer;
}
.group-head .grip {
  color: rgba(255, 255, 255, 0.6);
  font-size: 16px;
  letter-spacing: -3px;
}
.group-head .group-title-block {
  display: flex;
  flex-direction: column;
  margin-left: 4px;
  line-height: 1.25;
}
.group-head .group-title-text {
  color: #FFFFFF;
  font-size: 15px;
  font-weight: 600;
}
.group-head .group-meta {
  color: rgba(255, 255, 255, 0.8);
  font-size: 12px;
}
.group-head .spacer {
  flex: 1;
}
.group-head .head-ico {
  color: rgba(255, 255, 255, 0.85);
  padding: 2px 7px;
  font-size: 13px;
  border-radius: 6px;
  transition: 0.15s all ease;
}
.group-head .head-ico:hover {
  color: #009FE3;
  background: #fff;
}
.group-head .head-ico.danger:hover {
  color: #e55353;
  background: #fff;
}
.group-head .head-ico.disabled {
  opacity: 0.35;
  pointer-events: none;
}
.group-body {
  padding: 12px 14px 14px;
}
.group-body label {
  font-size: 13px;
  color: #5c6873;
  font-weight: 600;
  margin-bottom: 4px;
}
.group-body label.required::after {
  content: "*";
  color: red;
}
.group-addons-table {
  width: 100%;
  border-collapse: collapse;
}
.group-addons-table th {
  text-align: left;
  font-size: 12px;
  text-transform: uppercase;
  letter-spacing: 0.03em;
  color: #8a93a2;
  font-weight: 600;
  padding: 5px 8px;
  background: #F5F5F5;
  border-bottom: 1px solid #e8ecf0;
}
.group-addons-table th:first-child {
  border-radius: 6px 0 0 0;
}
.group-addons-table th:last-child {
  border-radius: 0 6px 0 0;
}
.group-addons-table td {
  padding: 6px 8px;
  border-bottom: 1px solid #f0f3f6;
  font-size: 14px;
  color: #3c4b64;
}
.group-addons-table tr:last-child td {
  border-bottom: none;
}
.group-addons-table th.r, .group-addons-table td.r {
  text-align: right;
  width: 110px;
}
.group-addons-table th.c, .group-addons-table td.c {
  text-align: center;
  width: 80px;
}
.group-addons-table th.w40, .group-addons-table td.w40 {
  width: 40px;
}
.group-addons-table td.no-default {
  font-style: italic;
  color: #8a93a2;
  font-size: 13px;
}
.group-addons-table .rm {
  color: #c3c9d0;
  cursor: pointer;
  font-weight: 700;
  padding: 2px 6px;
  border-radius: 6px;
  transition: 0.15s all ease;
}
.group-addons-table .rm:hover {
  color: #e55353;
  background: #fdf1f1;
}
.group-addons-table input[type="radio"] {
  accent-color: #009FE3;
  width: 16px;
  height: 16px;
  cursor: pointer;
}
.add-addon-controls select {
  min-width: 240px;
  border-color: #d8dbe0;
}
/* "Add" button in brand blue, mirrors .btn-primary.light (tinted bg, blue text) */
.add-addon-controls .btn-outline-primary {
  color: #009FE3;
  border-color: #009FE3;
  background: #fff;
  font-weight: 600;
  border-radius: 8px;
  padding: 4px 16px;
}
.add-addon-controls .btn-outline-primary:hover:not(:disabled) {
  color: #fff;
  background: #009FE3;
  border-color: #009FE3;
}
.add-addon-controls .btn-outline-primary:disabled {
  color: #9aa0a8;
  border-color: #d8dbe0;
  background: #fff;
}
</style>
